import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { PreApprovalStatus, WorkOrderDetail } from '../api/types.js';
import { useSession } from '../auth/SessionProvider.js';
import { PERMISSIONS, ROLE_CODES } from '../../shared/permissions.js';
import {
  Alert,
  Button,
  Card,
  DefinitionList,
  Field,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { formatCurrency, formatDateTime } from '../lib/format.js';

/**
 * The agreement's two pre-authorization obligations, shown to whichever side has to
 * act: USPS records the Facilities Response Line determination for repairs above the
 * referral threshold, and the Contracting Officer rules on competition. Neither side
 * sees VGS cost here — only the total USPS is being asked to authorize, which they
 * already see on the proposal.
 */
export function PreApprovalSection({ workOrder }: { workOrder: WorkOrderDetail }) {
  const queryClient = useQueryClient();
  const { can, isContractor, user } = useSession();
  const [error, setError] = useState<string | null>(null);

  const isContractingOfficer = user?.roleCodes.includes(ROLE_CODES.CUSTOMER_CO) ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ['pre-approval', workOrder.id],
    queryFn: () => apiRequest<PreApprovalStatus>(`/api/work-orders/${workOrder.id}/pre-approval`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['pre-approval', workOrder.id] });
    void queryClient.invalidateQueries({ queryKey: ['proposals', workOrder.id] });
    void queryClient.invalidateQueries({ queryKey: ['timeline', workOrder.id] });
  };

  const onError = (caught: unknown) =>
    setError(
      caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Try again.',
    );

  if (isLoading) return <Spinner label="Loading approval requirements" />;
  if (!data) return null;

  // Nothing to show until a priced proposal has triggered an obligation.
  if (!data.referralRequired && !data.competitionRequired && data.determination === null) {
    return isContractingOfficer ? (
      <Card title="Competition">
        {data.emergencyCallout && <EmergencyWaiverNote />}
        <CompetitionRequirementForm
          workOrderId={workOrder.id}
          required={data.competitionRequiredByCo}
          onDone={invalidate}
          onError={onError}
        />
      </Card>
    ) : null;
  }

  return (
    <div className="flex flex-col gap-5">
      {error && (
        <Alert variant="error" title="Could not save">
          {error}
        </Alert>
      )}

      {(data.referralRequired || data.determination) && (
        <Card title="USPS Facilities Response Line">
          <ResponseLinePanel
            workOrderId={workOrder.id}
            status={data}
            mayRecord={!isContractor || can(PERMISSIONS.WORK_ORDER_EDIT)}
            isContractingOfficer={isContractingOfficer}
            onDone={invalidate}
            onError={onError}
          />
        </Card>
      )}

      {data.emergencyCallout && !data.competitionRequired && (
        <Card title="Competition">
          <EmergencyWaiverNote />
          {isContractingOfficer && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <CompetitionRequirementForm
                workOrderId={workOrder.id}
                required={data.competitionRequiredByCo}
                onDone={invalidate}
                onError={onError}
              />
            </div>
          )}
        </Card>
      )}

      {data.competitionRequired && (
        <Card title="Competition">
          <CompetitionPanel
            workOrderId={workOrder.id}
            status={data}
            mayRequestException={isContractor && can(PERMISSIONS.PROPOSAL_MANAGE)}
            isContractingOfficer={isContractingOfficer}
            onDone={invalidate}
            onError={onError}
          />
          {isContractingOfficer && (
            <div className="mt-4 border-t border-slate-200 pt-4">
              <CompetitionRequirementForm
                workOrderId={workOrder.id}
                required={data.competitionRequiredByCo}
                onDone={invalidate}
                onError={onError}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function EmergencyWaiverNote() {
  return (
    <Alert variant="info" title="Three-quote requirement waived" className="mb-3">
      This work was performed under an emergency call-out before a proposal could be priced, so
      quotes could not be sought. The Contracting Officer may still require competition below.
    </Alert>
  );
}

const OUTCOME_LABELS: Record<string, string> = {
  pending: 'Awaiting determination',
  declined: 'Response Line declined the work',
  qualifies_as_project: 'Qualifies as a project',
};

function ResponseLinePanel({
  workOrderId,
  status,
  mayRecord,
  isContractingOfficer,
  onDone,
  onError,
}: {
  workOrderId: string;
  status: PreApprovalStatus;
  mayRecord: boolean;
  isContractingOfficer: boolean;
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const determination = status.determination;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    ticketNumber: determination?.ticketNumber ?? '',
    calledAt: '',
    outcome: (determination?.outcome === 'qualifies_as_project'
      ? 'qualifies_as_project'
      : 'declined') as 'declined' | 'qualifies_as_project',
    notes: '',
  });
  const [discretionComment, setDiscretionComment] = useState('');

  const record = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrderId}/response-line`, {
        method: 'PUT',
        body: {
          ticketNumber: form.ticketNumber.trim(),
          calledAt: new Date(form.calledAt).toISOString(),
          outcome: form.outcome,
          notes: form.notes.trim() || undefined,
        },
      }),
    onSuccess: () => {
      setEditing(false);
      onDone();
    },
    onError,
  });

  const grantDiscretion = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrderId}/response-line/co-discretion`, {
        method: 'POST',
        body: { comment: discretionComment.trim() },
      }),
    onSuccess: () => {
      setDiscretionComment('');
      onDone();
    },
    onError,
  });

  const threshold = formatCurrency(status.referralThresholdCents ?? 0);

  return (
    <div className="flex flex-col gap-4">
      {!determination && (
        <Alert variant="warning" title="A Response Line ticket is required">
          This repair is above the {threshold} referral threshold, so USPS personnel must call the
          Facilities Response Line at <strong>855-444-6375</strong> to create a ticket and determine
          whether the repair qualifies as a project. It cannot be approved until the outcome is
          recorded here.
        </Alert>
      )}

      {determination && (
        <DefinitionList
          columns={2}
          items={[
            { label: 'Ticket number', value: determination.ticketNumber },
            { label: 'Called', value: formatDateTime(determination.calledAt) },
            { label: 'Outcome', value: OUTCOME_LABELS[determination.outcome] },
            { label: 'Recorded by', value: determination.recordedByName },
            {
              label: 'CO discretion',
              value: determination.coDiscretionApproved
                ? `Granted by ${determination.coDiscretionByName ?? 'the Contracting Officer'} on ${formatDateTime(determination.coDiscretionAt)}`
                : 'Not granted',
            },
            { label: 'Notes', value: determination.notes },
          ]}
        />
      )}

      {determination?.outcome === 'qualifies_as_project' && (
        <Alert variant="error" title="This repair cannot be authorized here">
          The Response Line determined the work qualifies as a project, so it is handled outside the
          Reimbursable Expense process. Cancel this work order once USPS confirms how the project
          will proceed.
        </Alert>
      )}

      {determination?.outcome === 'declined' && !determination.coDiscretionApproved && (
        <Alert variant="warning" title="Contracting Officer discretion required">
          The Response Line declined the work. It may still be approved at the discretion of the
          Contracting Officer if doing so serves the best interest of the Postal Service.
        </Alert>
      )}

      {mayRecord && !editing && (
        <div>
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {determination ? 'Update determination' : 'Record determination'}
          </Button>
        </div>
      )}

      {mayRecord && editing && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            record.mutate();
          }}
        >
          <Field label="Response Line ticket number" htmlFor="rl-ticket" required>
            <TextInput
              id="rl-ticket"
              value={form.ticketNumber}
              required
              onChange={(event) => setForm({ ...form, ticketNumber: event.target.value })}
            />
          </Field>
          <Field label="Date and time of the call" htmlFor="rl-called" required>
            <TextInput
              id="rl-called"
              type="datetime-local"
              value={form.calledAt}
              required
              max={new Date().toISOString().slice(0, 16)}
              onChange={(event) => setForm({ ...form, calledAt: event.target.value })}
            />
          </Field>
          <Field label="Determination" htmlFor="rl-outcome" required>
            <Select
              id="rl-outcome"
              value={form.outcome}
              onChange={(event) =>
                setForm({ ...form, outcome: event.target.value as typeof form.outcome })
              }
            >
              <option value="declined">Response Line declined the work</option>
              <option value="qualifies_as_project">Qualifies as a project</option>
            </Select>
          </Field>
          <Field label="Notes" htmlFor="rl-notes">
            <TextArea
              id="rl-notes"
              value={form.notes}
              rows={3}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
            />
          </Field>
          <div className="flex gap-2">
            <Button type="submit" loading={record.isPending}>
              Save determination
            </Button>
            <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {isContractingOfficer &&
        determination?.outcome === 'declined' &&
        !determination.coDiscretionApproved && (
          <form
            className="flex flex-col gap-3 border-t border-slate-200 pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              grantDiscretion.mutate();
            }}
          >
            <Field
              label="Why approving this repair serves the best interest of the Postal Service"
              htmlFor="rl-discretion"
              required
            >
              <TextArea
                id="rl-discretion"
                value={discretionComment}
                rows={3}
                required
                minLength={10}
                onChange={(event) => setDiscretionComment(event.target.value)}
              />
            </Field>
            <div>
              <Button type="submit" loading={grantDiscretion.isPending}>
                Authorize at CO discretion
              </Button>
            </div>
          </form>
        )}
    </div>
  );
}

function CompetitionPanel({
  workOrderId,
  status,
  mayRequestException,
  isContractingOfficer,
  onDone,
  onError,
}: {
  workOrderId: string;
  status: PreApprovalStatus;
  mayRequestException: boolean;
  isContractingOfficer: boolean;
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const [justification, setJustification] = useState('');
  const [decisionComment, setDecisionComment] = useState('');

  const requestException = useMutation({
    mutationFn: () =>
      apiRequest(`/api/work-orders/${workOrderId}/competition-exceptions`, {
        method: 'POST',
        body: { justification: justification.trim() },
      }),
    onSuccess: () => {
      setJustification('');
      onDone();
    },
    onError,
  });

  const open = status.exceptions.find((exception) => exception.status === 'pending');

  const decide = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') =>
      apiRequest(`/api/competition-exceptions/${open?.id ?? ''}/decision`, {
        method: 'POST',
        body: { decision, comment: decisionComment.trim() || undefined },
      }),
    onSuccess: () => {
      setDecisionComment('');
      onDone();
    },
    onError,
  });

  const satisfied = status.quotesObtained >= status.quotesRequired;
  const waived = status.exceptions.some((exception) => exception.status === 'approved');

  return (
    <div className="flex flex-col gap-4">
      <Alert
        variant={satisfied || waived ? 'success' : 'warning'}
        title={
          satisfied
            ? 'Competition satisfied'
            : waived
              ? 'Competition exception approved'
              : `${status.quotesObtained} of ${status.quotesRequired} quotes on file`
        }
      >
        {status.competitionReason === 'contracting_officer'
          ? 'The Contracting Officer required competition on this work order.'
          : `Competition applies because this repair is above the ${formatCurrency(status.referralThresholdCents ?? 0)} referral threshold.`}{' '}
        {status.minimumQuotes} quotes are the minimum; fewer requires Contracting Officer approval.
      </Alert>

      {status.exceptions.length > 0 && (
        <ul className="flex flex-col gap-3">
          {status.exceptions.map((exception) => (
            <li key={exception.id} className="rounded-md border border-slate-200 p-3 text-sm">
              <div className="font-medium text-slate-900">
                {exception.status === 'pending'
                  ? 'Exception awaiting the Contracting Officer'
                  : `Exception ${exception.status}`}{' '}
                · {exception.quotesObtained} quotes obtained
              </div>
              <p className="mt-1 text-slate-700">{exception.justification}</p>
              <p className="mt-1 text-xs text-slate-500">
                Requested by {exception.requestedByName ?? 'VGS'} on{' '}
                {formatDateTime(exception.requestedAt)}
                {exception.decidedAt
                  ? ` · decided by ${exception.decidedByName ?? 'the Contracting Officer'} on ${formatDateTime(exception.decidedAt)}`
                  : ''}
              </p>
              {exception.decisionComment && (
                <p className="mt-1 text-slate-700">{exception.decisionComment}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {mayRequestException && !satisfied && !open && !waived && (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            requestException.mutate();
          }}
        >
          <Field
            label="Why adequate competition could not be obtained"
            htmlFor="ce-justification"
            required
            hint="The Contracting Officer must approve proceeding with fewer than the minimum quotes."
          >
            <TextArea
              id="ce-justification"
              value={justification}
              rows={3}
              required
              minLength={20}
              onChange={(event) => setJustification(event.target.value)}
            />
          </Field>
          <div>
            <Button type="submit" loading={requestException.isPending}>
              Request competition exception
            </Button>
          </div>
        </form>
      )}

      {isContractingOfficer && open && (
        <div className="flex flex-col gap-3 border-t border-slate-200 pt-4">
          <Field label="Comment" htmlFor="ce-comment" hint="Required when rejecting.">
            <TextArea
              id="ce-comment"
              value={decisionComment}
              rows={2}
              onChange={(event) => setDecisionComment(event.target.value)}
            />
          </Field>
          <div className="flex gap-2">
            <Button loading={decide.isPending} onClick={() => decide.mutate('approved')}>
              Approve exception
            </Button>
            <Button
              variant="secondary"
              loading={decide.isPending}
              onClick={() => decide.mutate('rejected')}
            >
              Reject exception
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompetitionRequirementForm({
  workOrderId,
  required,
  onDone,
  onError,
}: {
  workOrderId: string;
  required: boolean;
  onDone: () => void;
  onError: (caught: unknown) => void;
}) {
  const [comment, setComment] = useState('');

  const save = useMutation({
    mutationFn: (next: boolean) =>
      apiRequest(`/api/work-orders/${workOrderId}/competition-required`, {
        method: 'PUT',
        body: { required: next, comment: comment.trim() || undefined },
      }),
    onSuccess: () => {
      setComment('');
      onDone();
    },
    onError,
  });

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-slate-700">
        {required
          ? 'You have required competition on this work order.'
          : 'Multiple proposals are not required below the referral threshold unless you request them.'}
      </p>
      {!required && (
        <Field label="Note to VGS" htmlFor="cr-comment">
          <TextArea
            id="cr-comment"
            value={comment}
            rows={2}
            onChange={(event) => setComment(event.target.value)}
          />
        </Field>
      )}
      <div>
        <Button
          variant={required ? 'secondary' : 'primary'}
          loading={save.isPending}
          onClick={() => save.mutate(!required)}
        >
          {required ? 'Withdraw competition requirement' : 'Require competition'}
        </Button>
      </div>
    </div>
  );
}
